import { useEffect, useCallback, useState } from 'react';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import platformAtom from '~/recoil/atom/platform';
import globalDataStatusAtom from '~/recoil/atom/globalDataStatus';
import remotePickMealPointsListAtom from '~/recoil/atom/remotePickMealPointsList';
import remotePickMealPointAtom from '~/recoil/atom/remotePickMealPoint';
import displayPickMealPointListSelector from '~/recoil/selectors/displayPickMealPointList';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';

import { platformRemotePickMealPointsListener } from '~/firebase/listeners/platform';

import { PLATFORM_ID } from '~/globalVariables';

import * as fp from 'lodash/fp';

import getRecentPickMealTime from '~/utils/getRecentPickMealTime';

// types
import { FirebaseDataStatus } from '~/types/utilities';
import { CollectionSnapShotFn } from '~/firebase/listeners/type';
import { RemotePickMealPoint } from '~/types/platform/remotePickMealPoint';
import { useParams } from 'react-router-dom';

export default function useRemotePickMealPoints() {
  const [isInitData, setInitData] = useState(false);
  const platform = useRecoilValue(platformAtom);
  const [globalDataStatus, setGlobalDataStatus] = useRecoilState(globalDataStatusAtom);
  const [allRemotePickMealPointsList, setAllRemotePickMealPointsList] = useRecoilState(
    remotePickMealPointsListAtom,
  );
  const { enterpriseGroupBuyId = '' } = useParams();
  const displayPickMealPointList = useRecoilValue(displayPickMealPointListSelector);
  const setRemotePickMealPoint = useSetRecoilState(remotePickMealPointAtom);
  const setPickMealAt = useSetRecoilState(pickMealAtAtom);
  const setPickMealEnd = useSetRecoilState(pickMealEndAtom);

  const handleCollectionSnapShot = useCallback<CollectionSnapShotFn>(
    (snap) => {
      if (fp.eq(snap.docs.length, 0)) {
        setGlobalDataStatus((prev) => ({
          ...prev,
          remotePickMealPoints: FirebaseDataStatus.NOT_EXIST,
        }));
        return;
      }

      setGlobalDataStatus((prev) => ({ ...prev, remotePickMealPoints: FirebaseDataStatus.EXIST }));

      const remotePickMealPoints = snap.docs
        .map((doc) => {
          return {
            id: doc.id,
            ...(doc.data() as RemotePickMealPoint),
          };
        })
        .filter(({ id }) => platform.remotePickMealPoints.includes(id))
        .sort((a, b) => {
          const aIndex = platform.remotePickMealPoints.findIndex((id) => a.id === id);
          const bIndex = platform.remotePickMealPoints.findIndex((id) => b.id === id);
          return aIndex - bIndex;
        });

      setAllRemotePickMealPointsList(remotePickMealPoints);
    },
    [setAllRemotePickMealPointsList, setGlobalDataStatus, platform.remotePickMealPoints],
  );

  useEffect(() => {
    if (fp.eq(globalDataStatus.platform, FirebaseDataStatus.EXIST)) {
      const unlisten = platformRemotePickMealPointsListener(PLATFORM_ID, handleCollectionSnapShot);
      return () => {
        unlisten();
      };
    }
  }, [globalDataStatus.platform, handleCollectionSnapShot]);

  useEffect(() => {
    if (
      fp.eq(isInitData, false) &&
      fp.eq(globalDataStatus.platform, FirebaseDataStatus.EXIST) &&
      fp.eq(globalDataStatus.cart, FirebaseDataStatus.INIT) &&
      fp.gt(displayPickMealPointList.length, 0)
    ) {
      const remotePickMealPoint = displayPickMealPointList[0];
      setRemotePickMealPoint({
        id: remotePickMealPoint.id,
        name: remotePickMealPoint.name,
        address: remotePickMealPoint.location.address,
        geoPoint: remotePickMealPoint.location.geoPoint,
      });
      const recentPickMealTime = getRecentPickMealTime(
        platform.preOrderSetting,
        remotePickMealPoint.operatingTime,
        new Date(),
      );

      if (!enterpriseGroupBuyId) {
        setPickMealAt(recentPickMealTime.at);
        setPickMealEnd(recentPickMealTime.end);
      }
      setInitData(true);
    }
  }, [
    isInitData,
    globalDataStatus,
    displayPickMealPointList,
    platform.remotePickMealPoints,
    platform.preOrderSetting,
  ]);
}
